import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import HubRiskItem from './HubRiskItem';
import { byHandledAtDesc } from '../utils/riskSort';

const MAX_TASK_ROWS = 3;

interface HubNotFinishedTask {
  taskId: number;
  taskCode?: string;
  title?: string;
  status?: string | null;
  priority?: string | null;
  isOverdue: boolean;
  overdueMinutes?: number | null;
  windowEndAt?: string | null;
  totalCount?: number | null;
}

interface HubWarehouseCardProps {
  warehouse: {
    warehouseId: number;
    warehouseName: string;
    kpis: Array<{
      metricKey: string;
      valueText: string;
      dodChangeText?: string;
      severity: string;
    }>;
    taskDoneCount: number;
    taskTotalCount: number;
    notFinishedTasks: HubNotFinishedTask[];
    risks: any[];
    /** 未处理的周期复盘风险（open 状态的 AGENT_PERIOD 条目），与已处理的 risks 不重叠 */
    unresolvedPeriodRisks?: any[];
    unfinishedTaskCount?: number;
    riskCount?: number;
    severity?: string | null;
    supplementaryNote?: string | null;
  };
  /** 服务端当前时刻（ISO-8601），与 reportDeadline 比较判定风险块是否到点展示 */
  serverNow: string;
  /** 日报截止时刻（ISO-8601），hub 本地默认 21:00；未到前风险块整块隐藏 */
  reportDeadline?: string | null;
  /** 是否展示实时预警（AGENT_LIVE）—— Nacos 灰度开关 */
  showLive: boolean;
  /** 是否展示周期复盘（AGENT_PERIOD）—— Nacos 灰度开关 */
  showPeriod: boolean;
}

/** 仓卡底色与左边条统一浅紫，与页面顶部 headline 同源 */
const CARD_ACCENT = 'border-l-violet-400 bg-gradient-to-r from-violet-50 to-purple-50';

/** 芯片四档染色，与 KPI 条、「历史包裹追踪」页同一套分档 */
const KPI_CHIP_CLASS: Record<string, string> = {
  EXCELLENT: 'border-green-200 bg-green-50 text-green-600',
  GOOD: 'border-blue-200 bg-blue-50 text-blue-600',
  WARNING: 'border-yellow-200 bg-yellow-50 text-yellow-600',
  CRITICAL: 'border-red-200 bg-red-50 text-red-600',
};

const kpiChipClass = (severity: string): string =>
  KPI_CHIP_CLASS[severity] || 'border-gray-200 bg-white text-gray-700';

/**
 * 状态标签由 isOverdue 本地推导 —— 后端不下发成品文案。
 * 未完成任务不区分开工与否，只分逾期与进行中：hub manager 关心的是「该催了」，不是「开工没」。
 */
const statusLabelOf = (task: HubNotFinishedTask): string =>
  (task.isOverdue ? 'OVERDUE' : 'STARTED');

const statusChipClass = (statusLabel: string): string =>
  (statusLabel === 'OVERDUE'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-amber-200 bg-amber-50 text-amber-700');

// 进度条恒绿（emerald-500），未完成量的紧迫感由左边条与「待办 x/y」承担
const PROGRESS_COLOR = 'bg-emerald-500';

const HubWarehouseCard: React.FC<HubWarehouseCardProps> = ({
  warehouse,
  serverNow,
  reportDeadline,
  showLive,
  showPeriod,
}) => {
  const { t } = useTranslation();
  const [tasksExpanded, setTasksExpanded] = useState(false);

  const metricLabels: Record<string, string> = {
    PICKING_RATE: String(t('workbench.metrics.pickingRate')),
    DELIVERY_RATE: String(t('workbench.metrics.deliveryRate')),
    RETURN_RATE: String(t('workbench.metrics.returnRate')),
    NO_UPDATE: String(t('workbench.metrics.noUpdateShort')),
  };

  const taskTitle = (task: HubNotFinishedTask): string => {
    if (task.title) {
      return task.title;
    }
    return String(t(`workbench.tasks.${task.taskCode}.name`, { defaultValue: task.taskCode || 'Task' }));
  };

  const unfinishedTotal = warehouse.unfinishedTaskCount ?? warehouse.notFinishedTasks.length;
  // 未处理周期复盘风险与已处置风险来自不同源，合成一份供渲染 —— 口径同工作台日报「周期复盘 · N / 已关闭 · M」
  const allRisks = [...(warehouse.unresolvedPeriodRisks ?? []), ...warehouse.risks];
  // 口径同仓管工作台待办：按上游 basic 返回的次序展示，保证 hub 和仓管两端顺序一致
  const shownTasks = tasksExpanded
    ? warehouse.notFinishedTasks
    : warehouse.notFinishedTasks.slice(0, MAX_TASK_ROWS);
  // 上游按仓内顺序给，不按处置时刻；口径同工作台日报的风险列表
  // 按 Nacos 灰度开关过滤：LIVE / PERIOD 任一关掉时对应 source 的条目直接不展示
  const visibleRisks = allRisks.filter((r: any) => {
    if (r.source === 'AGENT_LIVE') return showLive;
    if (r.source === 'AGENT_PERIOD') return showPeriod;
    return true;
  });
  const sortedRisks = [...visibleRisks].sort(byHandledAtDesc);
  // 「风险及处理情况 · N」的 N 按当前灰度下真正会渲染的条数出，否则关掉 PERIOD 时数字对不上肉眼条目
  const riskTotal = visibleRisks.length;
  // 未到 hub 本地日报截止时刻（默认 21:00）前，风险数据仍在实时聚合中，右侧整块隐藏
  // reportDeadline 缺失（上游未返回）时不做门禁，避免灰度期页面空白
  const risksVisible = !reportDeadline || Date.parse(serverNow) >= Date.parse(reportDeadline);
  // 折叠条数按已下发的任务算，不用 unfinishedTaskCount：上游每仓按
  // TaskConstants.HUB_REVIEW_PER_WAREHOUSE 截断下发，按总数算会承诺出展不开的条目
  const collapsibleCount = warehouse.notFinishedTasks.length - MAX_TASK_ROWS;
  const percent = warehouse.taskTotalCount > 0
    ? Math.round((warehouse.taskDoneCount / warehouse.taskTotalCount) * 100)
    : 0;

  return (
    <div className={`mb-4 rounded-lg border border-violet-200 border-l-[3px] p-4 ${CARD_ACCENT}`}>
      {/* 仓头：仓名 */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-base font-bold text-gray-900">{warehouse.warehouseName}</h3>
      </div>

      {/* KPI 芯片行 + 待办进度 */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="flex gap-2 flex-wrap min-w-0">
          {warehouse.kpis.map((kpi) => (
            // 环比暂时下线：数仓 SLA 表按日聚合，同窗口对比要等小时切片
            <span
              key={kpi.metricKey}
              className={`text-xs px-2 py-0.5 rounded border ${kpiChipClass(kpi.severity)}`}
            >
              {metricLabels[kpi.metricKey] || kpi.metricKey} {kpi.valueText}
            </span>
          ))}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-gray-500">
            {String(t('hubReview.pending'))} {warehouse.taskDoneCount}/{warehouse.taskTotalCount}
          </span>
          <div className="h-1.5 w-28 rounded-full overflow-hidden bg-gray-200">
            <div
              className={`h-full rounded-full ${PROGRESS_COLOR}`}
              style={{ width: `${percent}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 pt-3 border-t border-violet-200">
        {/* 左：未完成任务，默认只列前 3 条，其余折叠 */}
        <div className="lg:col-span-5">
          <p className="text-xs text-gray-500 mb-2">
            {String(t('hubReview.notFinishedCount', { count: unfinishedTotal }))}
          </p>
          {shownTasks.length > 0 ? (
            <div className="space-y-1.5">
              {/* 展开后条数不可控（上游每仓下发上限 50 条），列表内滚，
                  否则一个仓就能把片区页其余仓卡挤出首屏 */}
              <div
                className={`space-y-1.5 ${tasksExpanded ? 'max-h-64 overflow-y-auto pr-1' : ''}`}
              >
                {shownTasks.map((task) => (
                  <div
                    key={task.taskId}
                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white rounded border border-violet-200"
                  >
                    <span className="text-sm text-gray-800 truncate">{taskTitle(task)}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      {task.totalCount !== null && task.totalCount !== undefined && (
                        <span className="text-xs text-gray-500">
                          {String(t('workbench.taskText.volume', { count: task.totalCount }))}
                        </span>
                      )}
                      <span
                        className={`text-[11px] px-1.5 py-px rounded border ${statusChipClass(statusLabelOf(task))}`}
                      >
                        {String(t(`hubReview.statusLabel.${statusLabelOf(task)}`, {
                          defaultValue: statusLabelOf(task),
                        }))}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
              {collapsibleCount > 0 && (
                <button
                  type="button"
                  className="text-xs text-violet-600 hover:underline pt-1"
                  onClick={() => setTasksExpanded((prev) => !prev)}
                >
                  {String(tasksExpanded
                    ? t('hubReview.collapseTasks')
                    : t('hubReview.moreTasks', { count: collapsibleCount }))}
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400">{String(t('hubReview.allTasksDone'))}</p>
          )}
        </div>

        {/* 右：风险及处理情况；两个灰度开关都关时整块不渲染；
            未到日报截止时刻（PRD：hub 本地默认 21:00 前）时区块保留，但内容替换为「仓库作业中，请等待」
            —— 数据仍在实时聚合，此时给数字会误导 hub manager；卡片整体保持常驻 */}
        {(showLive || showPeriod) && (
        <div className="lg:col-span-7">
          <p className="text-xs text-gray-500 mb-2">
            {String(t('hubReview.risksAndHandling'))}
            {risksVisible ? ` · ${riskTotal}` : ''}
          </p>
          {risksVisible ? (
            // 条数与单条处理说明的长度都不可控，超出即列表内滚，
            // 否则一个仓就能把片区页其余仓卡挤出首屏（口径同左侧未完成任务列表）。
            // 补充说明虽非风险条目，但要与风险一起滚 —— 钉在滚动区外会遮住第一条风险
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
              {warehouse.supplementaryNote?.trim() && (
                <div className="border-l-2 border-violet-200 pl-3">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                    {warehouse.supplementaryNote}
                  </p>
                </div>
              )}
              {sortedRisks.length > 0 ? (
                sortedRisks.map((risk) => (
                  <HubRiskItem
                    key={risk.alertId}
                    risk={risk}
                    warehouseName={warehouse.warehouseName}
                  />
                ))
              ) : (
                <p className="text-xs text-gray-400">{String(t('hubReview.noRisks'))}</p>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400">{String(t('hubReview.risksBeforeDeadline'))}</p>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default HubWarehouseCard;
